import React, { useState } from 'react';
import { FileTextIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function Reports() {
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const reportCards = [{
    id: 'permits',
    title: 'Permit Issuance Summary',
    description: 'Overview of permits issued by type and period'
  }, {
    id: 'trips',
    title: 'Trip Activity Report',
    description: 'Forest entry and exit tracking statistics'
  }, {
    id: 'beneficiaries',
    title: 'Beneficiary Statistics',
    description: 'Demographics and registration trends'
  }, {
    id: 'compliance',
    title: 'Compliance & Cases Report',
    description: 'Offense tracking and resolution status'
  }, {
    id: 'revenue',
    title: 'Revenue Collection Report',
    description: 'Fees and fines collected'
  }, {
    id: 'performance',
    title: 'Station Performance Report',
    description: 'Performance metrics by station'
  }];
  const sampleData = [{
    month: 'Oct',
    value: 520
  }, {
    month: 'Nov',
    value: 680
  }, {
    month: 'Dec',
    value: 590
  }, {
    month: 'Jan',
    value: 720
  }, {
    month: 'Feb',
    value: 650
  }, {
    month: 'Mar',
    value: 780
  }];
  return <div className="space-y-6" data-id="element-2196" data-mp="2tm4tiqej23vz">
      <div data-id="element-2197" data-mp="2tlv2r0lnrsp8">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2198" data-mp="2tnfwwp4299mk">
          Reports & Analytics
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2199" data-mp="2ou3ljgoe6zbr">
          Generate and export system reports
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2200" data-mp="2ks050dmv94jm">
        <div className="flex items-center gap-4" data-id="element-2201" data-mp="3hd0gna81gaww">
          <div className="flex-1" data-id="element-2202" data-mp="3ckh2oc5fvdrk">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2203" data-mp="3cjfssb05sxbf">
              Date Range
            </label>
            <div className="flex gap-2" data-id="element-2204" data-mp="1apjmx54roktq">
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2205" data-mp="1fn30e2tn4tkm" />
              <span className="flex items-center text-gray-600" data-id="element-2206" data-mp="15wgp2ksyip7q">to</span>
              <input type="date" className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2207" data-mp="15udr9elkbrsg" />
            </div>
          </div>
          <div data-id="element-2208" data-mp="hz1cfj1prh6a">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2209" data-mp="hzkur0va07ku">
              Report Type
            </label>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2210" data-mp="mynpnshjkxf3">
              <option value="" data-id="element-2211" data-mp="2capx81ummdyl">All Types</option>
              <option value="permits" data-id="element-2212" data-mp="2hc5ctee1bs9l">Permits</option>
              <option value="trips" data-id="element-2213" data-mp="2h9j77kapfmu0">Trips</option>
              <option value="compliance" data-id="element-2214" data-mp="5ple1ous9vyg">Compliance</option>
            </select>
          </div>
          <div data-id="element-2215" data-mp="qi9s6mmntrj">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2216" data-mp="qs3o5asml2i">
              Station/Range
            </label>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2217" data-mp="fgg474b5hfp6">
              <option value="" data-id="element-2218" data-mp="oerrawct5vcv">All Stations</option>
              <option value="chandpai" data-id="element-2219" data-mp="od89307lda8g">Chandpai Range</option>
              <option value="katka" data-id="element-2220" data-mp="ke7u5ss8irqq">Katka Station</option>
              <option value="dhangmari" data-id="element-2221" data-mp="kfsnxg6q66nj">Dhangmari Station</option>
            </select>
          </div>
          <div className="self-end" data-id="element-2222" data-mp="yav1mphtd0aw">
            <button className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32]" data-id="element-2223" data-mp="tduvkekyrxjs">
              <DownloadIcon size={16} data-id="element-2224" data-mp="305dkpyqvgpnw" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Report Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2225" data-mp="2wmohtzotx6xn">
        {reportCards.map(report => <button key={report.id} onClick={() => setSelectedReport(report.id)} className={`bg-white rounded-lg border-2 p-6 text-left transition-all hover:shadow-lg ${selectedReport === report.id ? 'border-[#1B5E20] bg-green-50' : 'border-gray-200'}`} data-id="element-2226" data-mp="31l84k2b8ymr2">
            <FileTextIcon className={`mb-3 ${selectedReport === report.id ? 'text-[#1B5E20]' : 'text-gray-400'}`} size={32} data-id="element-2227" data-mp="2dwow9ms769yz" />
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-2228" data-mp="2isx6clrjfcza">{report.title}</h3>
            <p className="text-sm text-gray-600" data-id="element-2229" data-mp="2nt1rip3e4vvu">{report.description}</p>
          </button>)}
      </div>

      {/* Report View */}
      {selectedReport && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2230" data-mp="1v4meu97qepno">
          <div className="flex items-center justify-between mb-6" data-id="element-2231" data-mp="204ifbyh7zyyh">
            <h3 className="text-xl font-semibold text-gray-900" data-id="element-2232" data-mp="202nyowczet07">
              {reportCards.find(r => r.id === selectedReport)?.title}
            </h3>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2233" data-mp="d93uczq1xovf">
              <DownloadIcon size={16} data-id="element-2234" data-mp="87eg7b7j8v34" />
              Download PDF
            </button>
          </div>

          {/* Sample Chart */}
          <div className="mb-6" data-id="element-2235" data-mp="3c90iu4q4cnh">
            <ResponsiveContainer width="100%" height={300} data-id="element-2236" data-mp="3ugfpvm5lfa3z">
              <BarChart data={sampleData} data-id="element-2237" data-mp="3ui0n5jjal75j">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2238" data-mp="3poeiadjde7ka" />
                <XAxis dataKey="month" data-id="element-2239" data-mp="3pnmkqx67pzrd" />
                <YAxis data-id="element-2240" data-mp="3ln2m1p5zqo34" />
                <Tooltip data-id="element-2241" data-mp="3lodqzsb6a1vt" />
                <Bar dataKey="value" fill="#1B5E20" data-id="element-2242" data-mp="3llhtg538lf7w" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sample Table */}
          <div className="overflow-x-auto" data-id="element-2243" data-mp="3bod5q0tqtboz">
            <table className="w-full" data-id="element-2244" data-mp="3briutg2dgdkp">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-2245" data-mp="36qbq5pvizui9">
                <tr data-id="element-2246" data-mp="2x392pu5d8rj3">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2247" data-mp="2x2pux69fjjis">
                    Period
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2248" data-mp="31zzishh67n4i">
                    Total
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2249" data-mp="2s34mm58ddyte">
                    Approved
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2250" data-mp="3fvtge4chuq2b">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase" data-id="element-2251" data-mp="370418d1n2rxd">
                    Rejected
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2252" data-mp="2mp54mqmko9p">
                {sampleData.map((row, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-2253" data-mp="7ep52f6p3h6a" data-mpi={idx}>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2254" data-mp="1s3sz2000vr7v">
                      {row.month} 2024
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-2255" data-mp="1n3yba5bjnhr1">
                      {row.value}
                    </td>
                    <td className="px-4 py-3 text-sm text-green-600" data-id="element-2256" data-mp="21t433881y5ow">
                      {Math.floor(row.value * 0.85)}
                    </td>
                    <td className="px-4 py-3 text-sm text-yellow-600" data-id="element-2257" data-mp="194otj17owfky">
                      {Math.floor(row.value * 0.1)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600" data-id="element-2258" data-mp="147ybw9g8lbxf">
                      {Math.floor(row.value * 0.05)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}